import { TRPCClientError } from "@trpc/client"
import type { MutationMeta, QueryMeta } from "@tanstack/react-query"
import { reportErrorMessage } from "#/utils/ErrorReporting"
import ResponseError from "#/types/responseError"

// Type the react-query `meta` bag app-wide: a query/mutation may declare an `errorReport` for the
// global onError handler to send to Sentry. Standard react-query meta pattern.
declare module "@tanstack/query-core" {
  interface QueryMeta {
    errorReport?: { message: string; tags?: Record<string, string>; level?: "error" | "warning" }
  }
  interface MutationMeta {
    errorReport?: { message: string; tags?: Record<string, string>; level?: "error" | "warning" }
  }
}

function describeError(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`
  try {
    return String(error)
  } catch {
    return "unserializable error"
  }
}

// A `TRPCClientError` without `data.httpStatus` means the request never got a response — the
// browser Fetch API rejected before hitting the server (offline, DNS, CORS, request aborted on
// navigation/backgrounding). Filter here rather than Sentry's `beforeSend`: the captured
// exception is a synthetic `Error(report.message)`, not the underlying tRPC error.
function isClientSideNetworkAbort(error: unknown): boolean {
  if (!(error instanceof TRPCClientError)) return false
  const data = (error as TRPCClientError<never>).data as { httpStatus?: number } | undefined
  return data?.httpStatus === undefined
}

// Reports a failed query/mutation to Sentry if it declared an `errorReport` on its `meta`; no-ops
// otherwise. Feature-agnostic — the report's `message` is the Sentry title and grouping key, its tags
// are for filtering.
export function reportTaggedQueryError(error: unknown, meta: QueryMeta | MutationMeta | undefined): void {
  const report = meta?.errorReport
  // Never let a malformed report throw out of the cache onError and crash the app — skip if there's
  // no message to report.
  if (!report?.message) return
  if (isClientSideNetworkAbort(error)) return
  reportErrorMessage(report.message, {
    level: report.level ?? "error",
    fingerprint: [report.message],
    tags: error instanceof ResponseError ? { ...report.tags, correlationId: error.correlationId } : report.tags,
    extra: { error: describeError(error) }
  })
}
